import React from 'react';
import { Link } from 'wouter';
import { LectureDownloader } from '@/components/LectureDownloader';
import { 
  ChevronLeft, 
  Lightbulb, 
  AlertTriangle, 
  Info, 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle,
  Clock,
  Share2,
  BookOpen,
  FileText,
  Book,
  Dna,
  BrainCircuit,
  Scale,
  Pill,
  Heart,
  Beaker,
  Activity,
  Target,
  Zap,
  RefreshCw,
  TimerReset,
  PlusCircle,
  MinusCircle,
  Check,
  X,
  Download,
  Sprout,
  TrendingUp,
  Users,
  Brain,
  Shield,
  Flame
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";

// SHIFT Framework SVG Component
const SHIFTFrameworkDiagram = () => (
  <svg viewBox="0 0 800 600" className="w-full h-96 mx-auto">
    <defs>
      <linearGradient id="shiftGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.1"/>
        <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1"/>
      </linearGradient>
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="2" dy="2" stdDeviation="3" stopOpacity="0.3"/>
      </filter>
    </defs>
    
    {/* Central Circle */}
    <circle cx="400" cy="300" r="80" fill="url(#shiftGradient)" stroke="#3b82f6" strokeWidth="2" filter="url(#shadow)"/>
    <text x="400" y="305" textAnchor="middle" className="fill-slate-700 text-lg font-semibold">SHIFT</text>
    
    {/* Five Pillars */}
    <g>
      {/* Sensing */}
      <circle cx="400" cy="120" r="60" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2"/>
      <text x="400" y="115" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">Sensing &</text>
      <text x="400" y="130" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">Metabolic</text>
      <line x1="400" y1="180" x2="400" y2="220" stroke="#3b82f6" strokeWidth="2"/>
      
      {/* Homeostasis */}
      <circle cx="600" cy="250" r="60" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
      <text x="600" y="245" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">Homeostatic</text>
      <text x="600" y="260" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">Defense</text>
      <line x1="540" y1="275" x2="480" y2="300" stroke="#3b82f6" strokeWidth="2"/>
      
      {/* Inflammation */}
      <circle cx="550" cy="450" r="60" fill="#fecaca" stroke="#ef4444" strokeWidth="2"/>
      <text x="550" y="445" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">Inflammation</text>
      <text x="550" y="460" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">& Immune</text>
      <line x1="490" y1="425" x2="445" y2="360" stroke="#3b82f6" strokeWidth="2"/>
      
      {/* Fibrosis */}
      <circle cx="250" cy="450" r="60" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2"/>
      <text x="250" y="445" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">Fibrosis /</text>
      <text x="250" y="460" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">Structural</text>
      <line x1="310" y1="425" x2="355" y2="360" stroke="#3b82f6" strokeWidth="2"/>
      
      {/* Timekeeping */}
      <circle cx="200" cy="250" r="60" fill="#fce7f3" stroke="#ec4899" strokeWidth="2"/>
      <text x="200" y="245" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">Timekeeping</text>
      <text x="200" y="260" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">& Chrono</text>
      <line x1="260" y1="275" x2="320" y2="300" stroke="#3b82f6" strokeWidth="2"/>
    </g>
  </svg>
);

// FGF21 Pathway Diagram
const FGF21PathwayDiagram = () => (
  <svg viewBox="0 0 800 500" className="w-full h-80 mx-auto">
    <defs>
      <linearGradient id="fgf21Gradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#22c55e"/>
        <stop offset="100%" stopColor="#16a34a"/>
      </linearGradient>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#22c55e"/>
      </marker>
    </defs>
    
    {/* Plant Foods */}
    <rect x="50" y="100" width="120" height="80" rx="10" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
    <text x="110" y="130" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">Plant-Based</text>
    <text x="110" y="150" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">Low Fat/Protein</text>
    
    {/* Arrow to FGF21 */}
    <line x1="170" y1="140" x2="250" y2="140" stroke="#22c55e" strokeWidth="3" markerEnd="url(#arrowhead)"/>
    
    {/* FGF21 */}
    <circle cx="300" cy="140" r="50" fill="url(#fgf21Gradient)" stroke="#16a34a" strokeWidth="3"/>
    <text x="300" y="135" textAnchor="middle" className="fill-white text-sm font-bold">FGF21</text>
    <text x="300" y="150" textAnchor="middle" className="fill-white text-xs">Longevity</text>
    
    {/* Multiple arrows to benefits */}
    <line x1="350" y1="120" x2="450" y2="80" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowhead)"/>
    <line x1="350" y1="140" x2="450" y2="140" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowhead)"/>
    <line x1="350" y1="160" x2="450" y2="200" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowhead)"/>
    <line x1="350" y1="180" x2="450" y2="260" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowhead)"/>
    
    {/* Benefits */}
    <rect x="450" y="50" width="140" height="40" rx="5" fill="#f0fdf4" stroke="#22c55e"/>
    <text x="520" y="75" textAnchor="middle" className="fill-slate-700 text-xs font-medium">Improved Insulin Sensitivity</text>
    
    <rect x="450" y="120" width="140" height="40" rx="5" fill="#f0fdf4" stroke="#22c55e"/>
    <text x="520" y="145" textAnchor="middle" className="fill-slate-700 text-xs font-medium">Reduced Inflammation</text>
    
    <rect x="450" y="180" width="140" height="40" rx="5" fill="#f0fdf4" stroke="#22c55e"/>
    <text x="520" y="205" textAnchor="middle" className="fill-slate-700 text-xs font-medium">Enhanced Autophagy</text>
    
    <rect x="450" y="240" width="140" height="40" rx="5" fill="#f0fdf4" stroke="#22c55e"/>
    <text x="520" y="265" textAnchor="middle" className="fill-slate-700 text-xs font-medium">Metabolic Optimization</text>
    
    {/* Blue Zone Connection */}
    <rect x="50" y="300" width="120" height="60" rx="10" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2"/>
    <text x="110" y="325" textAnchor="middle" className="fill-slate-700 text-sm font-semibold">Blue Zones</text>
    <text x="110" y="340" textAnchor="middle" className="fill-slate-700 text-xs">Longevity Hotspots</text>
    
    <line x1="170" y1="330" x2="250" y2="190" stroke="#3b82f6" strokeWidth="2" strokeDasharray="5,5"/>
    <text x="210" y="250" className="fill-slate-600 text-xs">Dietary Pattern</text>
  </svg>
);

// Intervention Impact Matrix
const InterventionMatrix = () => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse border border-slate-300">
      <thead>
        <tr className="bg-slate-50">
          <th className="border border-slate-300 p-3 text-left font-semibold text-sm">Intervention</th>
          <th className="border border-slate-300 p-3 text-center font-semibold text-sm">Sensing</th>
          <th className="border border-slate-300 p-3 text-center font-semibold text-sm">Homeostasis</th>
          <th className="border border-slate-300 p-3 text-center font-semibold text-sm">Inflammation</th>
          <th className="border border-slate-300 p-3 text-center font-semibold text-sm">Fibrosis</th>
          <th className="border border-slate-300 p-3 text-center font-semibold text-sm">Timekeeping</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="border border-slate-300 p-3 font-medium">Resistance Training</td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><X className="w-5 h-5 text-slate-400 mx-auto" /></td>
        </tr>
        <tr className="bg-slate-25">
          <td className="border border-slate-300 p-3 font-medium">Plant-Predominant Diet</td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><X className="w-5 h-5 text-slate-400 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><X className="w-5 h-5 text-slate-400 mx-auto" /></td>
        </tr>
        <tr>
          <td className="border border-slate-300 p-3 font-medium">GLP-1 Agonists</td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><X className="w-5 h-5 text-slate-400 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><X className="w-5 h-5 text-slate-400 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><X className="w-5 h-5 text-slate-400 mx-auto" /></td>
        </tr>
        <tr className="bg-slate-25">
          <td className="border border-slate-300 p-3 font-medium">Hormone Replacement</td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><X className="w-5 h-5 text-slate-400 mx-auto" /></td>
        </tr>
        <tr>
          <td className="border border-slate-300 p-3 font-medium">Testosterone</td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><X className="w-5 h-5 text-slate-400 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><X className="w-5 h-5 text-slate-400 mx-auto" /></td>
          <td className="border border-slate-300 p-3 text-center"><X className="w-5 h-5 text-slate-400 mx-auto" /></td>
        </tr>
      </tbody>
    </table>
  </div>
);

// Biomarker Panel Component
const BiomarkerPanel = ({ title, standard, advanced }: { title: string, standard?: string[], advanced?: string[] }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4">
    <h4 className="font-semibold text-slate-800 mb-3 flex items-center">
      <Beaker className="w-4 h-4 mr-2 text-blue-600" />
      {title}
    </h4>
    {standard && (
      <div className="mb-3">
        <h5 className="text-sm font-medium text-slate-600 mb-2">Standard Biomarkers</h5>
        <ul className="text-sm text-slate-700 space-y-1">
          {standard.map((marker, idx) => (
            <li key={idx} className="flex items-center">
              <CheckCircle className="w-3 h-3 mr-2 text-green-500" />
              {marker}
            </li>
          ))}
        </ul>
      </div>
    )}
    {advanced && (
      <div>
        <h5 className="text-sm font-medium text-slate-600 mb-2">Advanced Biomarkers</h5>
        <ul className="text-sm text-slate-700 space-y-1">
          {advanced.map((marker, idx) => (
            <li key={idx} className="flex items-center">
              <Target className="w-3 h-3 mr-2 text-blue-500" />
              {marker}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export default function WomensLongevityInterventions() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      <Navbar />
      
      {/* Header with Breadcrumbs */}
      <div className="container mx-auto px-4 py-6">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link href="/" className="text-slate-600 hover:text-slate-800">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link href="/mentorship" className="text-slate-600 hover:text-slate-800">Mentorship</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="text-slate-800 font-medium">
              Women's Longevity Interventions
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Title Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-4">
            The Top 5 Most Powerful Longevity Interventions for Women
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            A comprehensive SHIFT Framework approach to women's health in midlife, 
            targeting cellular signaling pathways for optimal longevity outcomes
          </p>
          <div className="flex items-center justify-center gap-4 mt-6 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>45 minutes</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span>Advanced</span>
            </div>
            <div className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              <span>17 slides</span>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <Tabs defaultValue="content" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="content" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Content
            </TabsTrigger>
            <TabsTrigger value="framework" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              SHIFT Framework
            </TabsTrigger>
            <TabsTrigger value="biomarkers" className="flex items-center gap-2">
              <Beaker className="w-4 h-4" />
              Biomarkers
            </TabsTrigger>
            <TabsTrigger value="download" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Resources
            </TabsTrigger>
          </TabsList>

          {/* Main Content */}
          <TabsContent value="content">
            <ScrollArea className="h-[80vh]">
              <div className="space-y-12">
                
                {/* Slide 1: Title */}
                <section className="bg-white rounded-lg border border-slate-200 p-8">
                  <div className="text-center">
                    <h2 className="text-3xl font-bold text-slate-800 mb-4">
                      The SHIFT Framework: A New Paradigm for Women's Health and Longevity in Midlife
                    </h2>
                    <div className="text-slate-600 space-y-2">
                      <p className="text-lg">Japan Conference on Women's Health</p>
                      <p>Moving from symptom management to cellular signaling optimization</p>
                    </div>
                  </div>
                </section>

                {/* Slide 2: Future of Medicine */}
                <section className="bg-white rounded-lg border border-slate-200 p-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
                    <BrainCircuit className="w-6 h-6 mr-3 text-blue-600" />
                    The Future of Medicine: From Symptoms to Signaling
                  </h2>
                  <div className="space-y-4 text-slate-700">
                    <p className="text-lg leading-relaxed">
                      For decades, medicine has focused on diagnosing and managing diseases based on 
                      symptoms and late-stage pathology.
                    </p>
                    <p className="leading-relaxed">
                      The future of medicine lies deeper—at the level of <strong>cellular signaling pathways</strong>. 
                      These are the intricate communication networks that govern every aspect of health, 
                      from metabolism to cellular repair.
                    </p>
                    <p className="leading-relaxed">
                      Aging itself is not a disease, but a consequence of predictable dysregulation 
                      within these core pathways.
                    </p>
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                      <p className="font-medium text-blue-800">
                        To truly impact healthspan and longevity, we need a framework that moves beyond 
                        symptom management and targets these fundamental biological processes directly.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Slide 3: Introducing SHIFT */}
                <section className="bg-white rounded-lg border border-slate-200 p-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
                    <Target className="w-6 h-6 mr-3 text-blue-600" />
                    Introducing the SHIFT Framework: A Map for a New Terrain
                  </h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-4 text-slate-700">
                      <p className="leading-relaxed">
                        The SHIFT framework is a proprietary model designed for this new era of medicine. 
                        It organizes the complex biology of aging into five core, interconnected pillars.
                      </p>
                      <p className="leading-relaxed">
                        It provides a structured, systems-based approach to longevity, allowing us to 
                        move beyond a "one-size-fits-all" model.
                      </p>
                      <p className="leading-relaxed">
                        By understanding these five pillars, we can identify and target the root causes 
                        of age-related decline in a personalized, precise manner.
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-800 mb-3">The Five Pillars:</h3>
                      <ul className="space-y-2 text-sm text-slate-700">
                        <li className="flex items-center">
                          <span className="w-6 h-6 bg-yellow-100 text-yellow-800 rounded-full flex items-center justify-center text-xs font-bold mr-3">S</span>
                          Sensing & Metabolic Fuel-Gauging
                        </li>
                        <li className="flex items-center">
                          <span className="w-6 h-6 bg-green-100 text-green-800 rounded-full flex items-center justify-center text-xs font-bold mr-3">H</span>
                          Homeostatic Defence & Repair
                        </li>
                        <li className="flex items-center">
                          <span className="w-6 h-6 bg-red-100 text-red-800 rounded-full flex items-center justify-center text-xs font-bold mr-3">I</span>
                          Inflammation & Immune Tone
                        </li>
                        <li className="flex items-center">
                          <span className="w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold mr-3">F</span>
                          Fibrosis / Structural Remodeling
                        </li>
                        <li className="flex items-center">
                          <span className="w-6 h-6 bg-pink-100 text-pink-800 rounded-full flex items-center justify-center text-xs font-bold mr-3">T</span>
                          Timekeeping & Chronobiology
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Slide 4: Deconstructing SHIFT */}
                <section className="bg-white rounded-lg border border-slate-200 p-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
                    <Dna className="w-6 h-6 mr-3 text-blue-600" />
                    Deconstructing the SHIFT Framework
                  </h2>
                  <div className="space-y-6">
                    <div className="grid gap-4">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <h3 className="font-semibold text-yellow-800 mb-2 flex items-center">
                          <span className="w-6 h-6 bg-yellow-200 text-yellow-800 rounded-full flex items-center justify-center text-sm font-bold mr-2">S</span>
                          Sensing & Metabolic Fuel-Gauging
                        </h3>
                        <p className="text-sm text-yellow-700">
                          How our cells sense and respond to nutrients and energy status. 
                          <span className="font-medium">(Insulin/IGF-1, mTOR, AMPK, Sirtuins)</span>
                        </p>
                      </div>
                      
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h3 className="font-semibold text-green-800 mb-2 flex items-center">
                          <span className="w-6 h-6 bg-green-200 text-green-800 rounded-full flex items-center justify-center text-sm font-bold mr-2">H</span>
                          Homeostatic Defence & Repair
                        </h3>
                        <p className="text-sm text-green-700">
                          The systems that protect our cells from damage and maintain their function. 
                          <span className="font-medium">(NRF2, p53/p16, autophagy/mitophagy)</span>
                        </p>
                      </div>
                      
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h3 className="font-semibold text-red-800 mb-2 flex items-center">
                          <span className="w-6 h-6 bg-red-200 text-red-800 rounded-full flex items-center justify-center text-sm font-bold mr-2">I</span>
                          Inflammation & Immune Tone
                        </h3>
                        <p className="text-sm text-red-700">
                          The delicate balance of the immune system, which can become dysregulated with age. 
                          <span className="font-medium">(NF-κB, JAK-STAT, senescence/SASP)</span>
                        </p>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
                          <span className="w-6 h-6 bg-blue-200 text-blue-800 rounded-full flex items-center justify-center text-sm font-bold mr-2">F</span>
                          Fibrosis / Structural Remodeling
                        </h3>
                        <p className="text-sm text-blue-700">
                          The process of tissue scarring and stiffening that occurs with aging. 
                          <span className="font-medium">(TGF-β, WNT/Notch, ECM cross-linking)</span>
                        </p>
                      </div>
                      
                      <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                        <h3 className="font-semibold text-pink-800 mb-2 flex items-center">
                          <span className="w-6 h-6 bg-pink-200 text-pink-800 rounded-full flex items-center justify-center text-sm font-bold mr-2">T</span>
                          Timekeeping & Chronobiology
                        </h3>
                        <p className="text-sm text-pink-700">
                          The internal clocks that regulate our physiology and are linked to the aging process. 
                          <span className="font-medium">(CLOCK/BMAL1, epigenetic clocks)</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Slide 6: Top 5 Interventions */}
                <section className="bg-white rounded-lg border border-slate-200 p-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
                    <TrendingUp className="w-6 h-6 mr-3 text-blue-600" />
                    The Top 5 Interventions for Women in Midlife
                  </h2>
                  <div className="space-y-4 text-slate-700">
                    <p className="leading-relaxed">
                      Based on the SHIFT framework and our biomarker assessment, we can identify a set of 
                      "Top 5" interventions that are particularly potent for women in midlife.
                    </p>
                    <p className="leading-relaxed">
                      These interventions are designed to work synergistically to address all five pillars of the framework.
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</div>
                        <Activity className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-blue-800 mb-2">Resistance Training</h3>
                      <p className="text-sm text-blue-700">The foundation for combating sarcopenia and improving metabolic health</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</div>
                        <Sprout className="w-5 h-5 text-green-600" />
                      </div>
                      <h3 className="font-semibold text-green-800 mb-2">Plant-Predominant Diet</h3>
                      <p className="text-sm text-green-700">Activating the FGF21 longevity signal through nutrient sensing</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</div>
                        <Pill className="w-5 h-5 text-purple-600" />
                      </div>
                      <h3 className="font-semibold text-purple-800 mb-2">Microdosed GLP-1</h3>
                      <p className="text-sm text-purple-700">Optimizing metabolic sensing with minimal intervention</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-pink-50 to-rose-50 border border-pink-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-pink-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</div>
                        <Heart className="w-5 h-5 text-pink-600" />
                      </div>
                      <h3 className="font-semibold text-pink-800 mb-2">Hormone Replacement</h3>
                      <p className="text-sm text-pink-700">Master regulator of cellular pathways and aging processes</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">5</div>
                        <Brain className="w-5 h-5 text-orange-600" />
                      </div>
                      <h3 className="font-semibold text-orange-800 mb-2">Testosterone</h3>
                      <p className="text-sm text-orange-700">Critical neurosteroid and metabolic regulator for women</p>
                    </div>
                  </div>
                </section>

                {/* Intervention Details */}
                <section className="space-y-8">
                  
                  {/* Resistance Training */}
                  <div className="bg-white rounded-lg border border-slate-200 p-8">
                    <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                      <Activity className="w-5 h-5 mr-3 text-blue-600" />
                      Intervention 1: Resistance Training
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-3">Mechanism of Action:</h3>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li>• Most potent intervention for combating sarcopenia</li>
                          <li>• Stimulates muscle protein synthesis (mTOR)</li>
                          <li>• Improves insulin sensitivity (AMPK)</li>
                          <li>• Enhances mitochondrial biogenesis</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-3">Clinical Protocol:</h3>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li>• <strong>Frequency:</strong> 2-3 times per week</li>
                          <li>• <strong>Intensity:</strong> 60-80% of 1-RM</li>
                          <li>• <strong>Focus:</strong> Compound exercises</li>
                          <li>• <strong>Exercises:</strong> Squats, deadlifts, overhead press</li>
                        </ul>
                      </div>
                    </div>
                    <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                      <p className="text-sm text-blue-800">
                        <strong>SHIFT Impact:</strong> Directly improves Sensing (insulin sensitivity), 
                        Homeostasis (muscle mass), Inflammation (reduces subclinical), and Fibrosis (bone density).
                      </p>
                    </div>
                  </div>

                  {/* Plant-Predominant Diet */}
                  <div className="bg-white rounded-lg border border-slate-200 p-8">
                    <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                      <Sprout className="w-5 h-5 mr-3 text-green-600" />
                      Intervention 2: Plant-Predominant Diet & FGF21 Longevity Signal
                    </h2>
                    <div className="grid lg:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <div>
                          <h3 className="font-semibold text-slate-700 mb-2">A New Paradigm:</h3>
                          <p className="text-sm text-slate-600">
                            Moving beyond calorie counting to "nutrient-sensing" diets that activate 
                            powerful longevity pathways.
                          </p>
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-700 mb-2">FGF21 - A Longevity 'Hormone':</h3>
                          <p className="text-sm text-slate-600">
                            Fibroblast Growth Factor 21 is a key metabolic regulator. Elevated FGF21 
                            is associated with improved metabolic health and increased lifespan.
                          </p>
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-700 mb-2">Activation Protocol:</h3>
                          <p className="text-sm text-slate-600">
                            Plant-predominant, high-carbohydrate (whole foods), low-fat (10-15%), 
                            moderate-to-low protein diet mimicking Blue Zone patterns.
                          </p>
                        </div>
                      </div>
                      <div>
                        <FGF21PathwayDiagram />
                      </div>
                    </div>
                  </div>

                  {/* GLP-1 Agonists */}
                  <div className="bg-white rounded-lg border border-slate-200 p-8">
                    <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                      <Pill className="w-5 h-5 mr-3 text-purple-600" />
                      Intervention 3: Microdosed GLP-1 Agonists
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-3">Mechanism & Impact:</h3>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li>• Mimics native incretin hormone</li>
                          <li>• Improves insulin secretion and sensitivity</li>
                          <li>• Suppresses glucagon release</li>
                          <li>• Reduces inflammatory visceral fat</li>
                          <li>• Regulates appetite and satiety</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-3">Microdosing Protocol:</h3>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li>• Start with sub-therapeutic doses</li>
                          <li>• Example: 0.125 mg semaglutide</li>
                          <li>• Titrate slowly as needed</li>
                          <li>• Monitor: HOMA-IR, hs-CRP, body composition</li>
                          <li>• Goal: Metabolic optimization, not just weight loss</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* HRT */}
                  <div className="bg-white rounded-lg border border-slate-200 p-8">
                    <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                      <Heart className="w-5 h-5 mr-3 text-pink-600" />
                      Intervention 4: Hormone Replacement Therapy
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-3">Master Regulatory Role:</h3>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li>• Replaces declining estrogen and progesterone</li>
                          <li>• Influences numerous cellular pathways</li>
                          <li>• Induces mitochondrial autophagy</li>
                          <li>• Delays vascular senescence</li>
                          <li>• Reduces vascular inflammation</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-3">Clinical Considerations:</h3>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li>• Individualized dosing and routes</li>
                          <li>• Optimal window: Under 60 years, within 10 years post-menopause</li>
                          <li>• Benefits outweigh risks for most healthy women</li>
                          <li>• Preserves bone density and skin collagen</li>
                          <li>• Profoundly pro-longevity therapy</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Testosterone */}
                  <div className="bg-white rounded-lg border border-slate-200 p-8">
                    <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                      <Brain className="w-5 h-5 mr-3 text-orange-600" />
                      Intervention 5: Testosterone for Cognitive & Metabolic Health
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-3">Reframing Testosterone:</h3>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li>• Critical neurosteroid and metabolic regulator</li>
                          <li>• Supports neuronal health and memory</li>
                          <li>• Enhances executive function</li>
                          <li>• Promotes lean muscle mass</li>
                          <li>• Crucial for bone mineral density</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-3">Clinical Protocol:</h3>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li>• Consider for fatigue and brain fog</li>
                          <li>• Difficulty maintaining muscle mass</li>
                          <li>• Start low, titrate to low-normal range</li>
                          <li>• Monitor symptoms and laboratory values</li>
                          <li>• Use alongside optimized HRT</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Synergistic Approach */}
                <section className="bg-white rounded-lg border border-slate-200 p-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
                    <Zap className="w-6 h-6 mr-3 text-blue-600" />
                    The Longevity Stack: A Synergistic Approach
                  </h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <p className="text-slate-700 leading-relaxed">
                        These interventions are not meant to be used in isolation. They are part of a 
                        "longevity stack" that can be tailored to the individual patient based on their 
                        unique biomarker data.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-blue-600 rounded mr-3"></div>
                          <span className="text-sm"><strong>Foundation:</strong> Resistance training and plant-predominant diet</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-purple-600 rounded mr-3"></div>
                          <span className="text-sm"><strong>Metabolic Optimization:</strong> Microdosed GLP-1 agonists</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-pink-600 rounded mr-3"></div>
                          <span className="text-sm"><strong>Hormonal Harmony:</strong> HRT and Testosterone</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-6">
                      <h3 className="font-semibold text-slate-800 mb-4">Personalization Approach:</h3>
                      <ul className="space-y-2 text-sm text-slate-600">
                        <li>• Begin with comprehensive biomarker assessment</li>
                        <li>• Establish baseline across all SHIFT pillars</li>
                        <li>• Layer interventions based on individual needs</li>
                        <li>• Monitor and adjust based on response</li>
                        <li>• Create sustainable, long-term protocols</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Conclusion */}
                <section className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
                    <CheckCircle className="w-6 h-6 mr-3 text-green-600" />
                    Conclusion: A New Paradigm for Women's Health
                  </h2>
                  <div className="space-y-4 text-slate-700">
                    <p className="text-lg leading-relaxed">
                      The future of medicine is here, and it is centered on cellular signaling.
                    </p>
                    <p className="leading-relaxed">
                      The SHIFT framework provides the map to navigate this new terrain.
                    </p>
                    <p className="leading-relaxed">
                      By measuring what matters (biomarkers) and intervening with targeted, 
                      synergistic therapies, we can empower our female patients to not just 
                      survive midlife, but to thrive for decades to come.
                    </p>
                    <div className="bg-white border border-blue-200 rounded-lg p-4 mt-6">
                      <p className="font-medium text-blue-800">
                        The midlife transition is a critical opportunity. By adopting a systems-based 
                        approach grounded in the science of cellular signaling, we can fundamentally 
                        change the aging trajectory for women.
                      </p>
                    </div>
                  </div>
                </section>

              </div>
            </ScrollArea>
          </TabsContent>

          {/* SHIFT Framework Tab */}
          <TabsContent value="framework">
            <div className="space-y-8">
              <div className="bg-white rounded-lg border border-slate-200 p-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center">
                  SHIFT Framework Visualization
                </h2>
                <SHIFTFrameworkDiagram />
                <div className="mt-8">
                  <h3 className="text-xl font-semibold text-slate-800 mb-4">Intervention Impact Matrix</h3>
                  <InterventionMatrix />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Biomarkers Tab */}
          <TabsContent value="biomarkers">
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  Measuring What Matters: Key Biomarkers of the SHIFT Pillars
                </h2>
                <p className="text-slate-600 max-w-3xl mx-auto">
                  To effectively intervene, we must first measure. Biomarkers allow us to assess 
                  the function of each SHIFT pillar and quantify a patient's biological age.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <BiomarkerPanel 
                  title="Sensing (S)"
                  standard={["HOMA-IR", "HbA1c", "Lipid Panel"]}
                  advanced={["Adiponectin", "Visceral Adipose Tissue (CT/MRI)"]}
                />
                
                <BiomarkerPanel 
                  title="Homeostasis (H)"
                  advanced={["8-OHdG (Urine)", "Glutathione", "p16INK4a expression"]}
                />
                
                <BiomarkerPanel 
                  title="Inflammation (I)"
                  standard={["hs-CRP"]}
                  advanced={["IL-6", "TNF-α", "MPO", "Lp-PLA2"]}
                />
                
                <BiomarkerPanel 
                  title="Fibrosis (F)"
                  standard={["Liver Function Tests (FIB-4)"]}
                  advanced={["Procollagen Peptides", "Pulse Wave Velocity", "Skin Autofluorescence"]}
                />
                
                <BiomarkerPanel 
                  title="Timekeeping (T)"
                  advanced={["Epigenetic Clocks (DunedinPACE)", "GrimAge", "DUTCH Test (Cortisol)"]}
                />
              </div>
            </div>
          </TabsContent>

          {/* Download Tab */}
          <TabsContent value="download">
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-slate-800 mb-4">Download Resources</h2>
                <p className="text-slate-600">Access presentation materials and supplementary resources</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <LectureDownloader 
                  lectureName="Complete Presentation"
                  fileName="womens-longevity-interventions-complete.pdf"
                />
                
                <LectureDownloader 
                  lectureName="SHIFT Framework Guide"
                  fileName="shift-framework-clinical-guide.pdf"
                />
                
                <LectureDownloader 
                  lectureName="Clinical Protocols"
                  fileName="womens-longevity-protocols.pdf"
                />
                
                <LectureDownloader 
                  lectureName="Biomarker Reference"
                  fileName="biomarker-reference-guide.pdf"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
}